import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useCart } from '@/context/CartContext';
import { ShoppingBag, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ShopPage() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, original, print
  const { addItem } = useCart();

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const { data } = await axios.get(`${API}/api/products`);
        setProducts(data);
      } catch (error) {
        console.error('Error fetching products:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  const filtered = filter === 'all' ? products : products.filter(p => p.product_type === filter);

  const handleAddToCart = (product) => {
    addItem(product);
    toast.success(`${product.title} added to cart`);
  };

  return (
    <div className="page-transition pt-28">
      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 pb-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-12">
          <div>
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#525252] block mb-2">
              Collection
            </span>
            <h1 className="font-serif text-5xl md:text-6xl text-[#F5F5F0] tracking-tight leading-none">
              Shop
            </h1>
          </div>

          {/* Filter tabs */}
          <div className="flex gap-1">
            {[
              { id: 'all', label: 'All' },
              { id: 'original', label: 'Originals' },
              { id: 'print', label: 'Prints' }
            ].map(f => (
              <button
                key={f.id}
                onClick={() => setFilter(f.id)}
                className={`px-5 py-2 font-mono text-[10px] uppercase tracking-[0.2em] transition-colors ${
                  filter === f.id
                    ? 'bg-[#C5A059] text-[#0A0A0A]'
                    : 'text-[#A3A3A3] border border-white/10 hover:text-[#F5F5F0]'
                }`}
                data-testid={`filter-${f.id}`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 pb-24">
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[1, 2, 3, 4, 5, 6].map(i => (
              <div key={i} className="aspect-square skeleton" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filtered.map((product) => (
              <div key={product.id} className="group" data-testid={`product-${product.id}`}>
                {/* Image */}
                <Link to={`/exhibition/${product.artwork_id}`} className="block relative overflow-hidden mb-4">
                  <img
                    src={product.image_url}
                    alt={product.title}
                    className="w-full h-[350px] object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute top-3 left-3">
                    <span className={`inline-block px-3 py-1 font-mono text-[10px] uppercase tracking-[0.15em] ${
                      product.product_type === 'original'
                        ? 'bg-[#C5A059] text-[#0A0A0A]'
                        : 'bg-white/10 text-[#F5F5F0] backdrop-blur-sm'
                    }`}>
                      {product.product_type}
                    </span>
                  </div>
                  {!product.in_stock && (
                    <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                      <span className="font-mono text-xs uppercase tracking-wider text-[#D84B79]">Sold Out</span>
                    </div>
                  )}
                </Link>

                {/* Info */}
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <h3 className="text-[#F5F5F0] text-sm font-medium mb-1 truncate">{product.title}</h3>
                    {product.edition_size && (
                      <p className="text-[#525252] text-xs font-mono">
                        Edition of {product.edition_size}
                      </p>
                    )}
                  </div>
                  <p className="text-[#C5A059] font-serif text-xl whitespace-nowrap">
                    ${product.price.toLocaleString()}
                  </p>
                </div>

                {/* Add to cart */}
                <button
                  onClick={() => handleAddToCart(product)}
                  disabled={!product.in_stock}
                  className="w-full mt-3 flex items-center justify-center gap-2 border border-white/10 py-3 text-[#A3A3A3] font-mono text-[10px] uppercase tracking-[0.2em] hover:border-[#C5A059] hover:text-[#C5A059] transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                  data-testid={`add-to-cart-${product.id}`}
                >
                  <Plus size={14} />
                  Add to Cart
                </button>
              </div>
            ))}
          </div>
        )}

        {!loading && filtered.length === 0 && (
          <p className="text-center text-[#525252] py-16 font-mono text-sm">No products found</p>
        )}
      </section>
    </div>
  );
}
